import type {
  Supplement,
  ClientSupplementAssignment,
  SupplementCategory,
  SupplementWithEffectiveNotes,
  PaginatedResponse,
} from '@/types/supplements';
import { api, withStatus } from '@/lib/apiClient';

// ─── Coach Supplement Library ─────────────────────────────────────────────────

export async function listCoachSupplements(
  page = 1,
  limit = 20,
  search?: string,
  category?: SupplementCategory,
): Promise<PaginatedResponse<Supplement>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.set('search', search);
  if (category) params.set('category', category);

  const res = await fetch(`/api/coach/supplements?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to list supplements (${res.status})`);
  const json = await res.json();
  return { data: json.data, total: json.total, page: json.page, limit: json.limit };
}

export async function createSupplement(data: {
  name: string;
  brand?: string | null;
  description?: string | null;
  url?: string | null;
  affiliate_code?: string | null;
  image_url?: string | null;
  default_usage_notes?: string | null;
  category?: SupplementCategory;
}): Promise<Supplement> {
  const res = await api.post<unknown>('/api/coach/supplements', data);
  if (!res.ok) {
    throw new Error(res.message || withStatus(res, (status) => `Failed to create supplement (${status})`));
  }
  return (res as { supplement?: Supplement }).supplement as Supplement;
}

export async function updateSupplement(
  id: string,
  data: Partial<Omit<Supplement, 'id' | 'coach_id' | 'created_at' | 'updated_at' | 'is_archived'>>,
): Promise<Supplement> {
  const res = await api.patch<unknown>(`/api/coach/supplements/${id}`, data);
  if (!res.ok) {
    throw new Error(res.message || withStatus(res, (status) => `Failed to update supplement (${status})`));
  }
  return (res as { supplement?: Supplement }).supplement as Supplement;
}

export async function archiveSupplement(id: string): Promise<void> {
  const res = await api.delete(`/api/coach/supplements/${id}`);
  if (!res.ok) {
    throw new Error(res.message || withStatus(res, (status) => `Failed to archive supplement (${status})`));
  }
}

// ─── Coach Assignment Management ──────────────────────────────────────────────

export async function listClientSupplementAssignments(
  clientId: string,
  page = 1,
  limit = 20,
): Promise<PaginatedResponse<ClientSupplementAssignment>> {
  const params = new URLSearchParams({
    clientId,
    page: String(page),
    limit: String(limit),
  });
  const res = await fetch(`/api/coach/supplement-assignments?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to list assignments (${res.status})`);
  const json = await res.json();
  return { data: json.data, total: json.total, page: json.page, limit: json.limit };
}

export async function assignSupplement(
  clientId: string,
  supplementId: string,
  customUsageNotes?: string | null,
): Promise<ClientSupplementAssignment> {
  const res = await api.post<unknown>('/api/coach/supplement-assignments', {
    clientId,
    supplementId,
    customUsageNotes: customUsageNotes || null,
  });
  if (res.status === 409) {
    throw new Error('This supplement is already assigned to this client');
  }
  if (!res.ok) {
    throw new Error(res.message || withStatus(res, (status) => `Failed to assign supplement (${status})`));
  }
  return (res as { assignment?: ClientSupplementAssignment }).assignment as ClientSupplementAssignment;
}

export async function updateAssignmentNotes(
  assignmentId: string,
  customUsageNotes: string | null,
): Promise<ClientSupplementAssignment> {
  const res = await api.patch<unknown>('/api/coach/supplement-assignments', { id: assignmentId, customUsageNotes });
  if (!res.ok) {
    throw new Error(res.message || withStatus(res, (status) => `Failed to update assignment (${status})`));
  }
  return (res as { assignment?: ClientSupplementAssignment }).assignment as ClientSupplementAssignment;
}

export async function removeSupplementAssignment(assignmentId: string): Promise<void> {
  const res = await api.delete(`/api/coach/supplement-assignments?id=${assignmentId}`);
  if (!res.ok) {
    throw new Error(res.message || withStatus(res, (status) => `Failed to remove assignment (${status})`));
  }
}

// ─── Client-Facing ────────────────────────────────────────────────────────────

export async function getMySupplements(page = 1, limit = 20): Promise<PaginatedResponse<SupplementWithEffectiveNotes>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const res = await fetch(`/api/clients/supplements?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to load supplements (${res.status})`);
  const json = await res.json();
  return { data: json.data, total: json.total, page: json.page, limit: json.limit };
}
